/**
 * RS Customizer — Mint Ornament
 * Three-part composited ornament (head + extension + tail) with letter insertion zone.
 * Spacer beads auto-fill the insertion zone when no text is entered.
 * Optional swing animation and sparkle overlay from metaobject settings.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  var METAOBJECT_TYPE = 'customizer_mint_ornament_set';
  var LIQUID_VIEW = 'metaobject-mint-ornament';

  var BASE_IMAGE_WIDTH = 1500;
  var ELEMENT_IMAGE_HEIGHT = 100;
  var SWING_MOTION_DEGREES = 10;
  var SWING_ANIMATION_DURATION = 4000;
  var SPARKLE_BASE_WIDTH = 60;
  var SPARKLE_DELTA_WIDTH = 25;

  var FONT_ICON_SCALE_FACTOR = 1;
  var BEAD_SCALE_FACTOR = 0.9;
  var EXTENDED_SCALE_FACTOR = 1;
  var CONTRACTED_SCALE_FACTOR = 1;
  var DEFAULT_INSERTION_ZONE_X = 70;
  var LETTER_COUNT_THRESHOLD = 8;
  var MIN_SPACER_WIDTH_RATIO = 0.6;
  var SPACER_DENSITY_FACTOR = 0.8;
  var MIN_SPACER_COUNT = 3;

  var _injectedSlideId = null;
  var _baseConfig = null;
  var _letters = null;
  var _backgroundsData = null;
  var _currentVariantTitle = null;
  var _isRendering = false;
  var _resizeTimer = null;
  var _iconMapping = {};

  // ─── Helpers ────────────────────────────────────────────────────

  function _getMtoText() {
    var fields = document.querySelectorAll('[data-mto-field]');
    var text = '';
    fields.forEach(function (field) { text += field.value; });
    return text.trim();
  }

  function _optimizeImageUrl(url, params) {
    if (!url) return url;
    var separator = url.indexOf('?') !== -1 ? '&' : '?';
    var paramString = Object.entries(params || {})
      .map(function (pair) { return pair[0] + '=' + pair[1]; })
      .join('&');
    return paramString ? url + separator + paramString : url;
  }

  function _triggerFadeIn(element) {
    requestAnimationFrame(function () {
      var h = element.offsetHeight; // force layout
      requestAnimationFrame(function () { element.classList.add('rs-mint-fade-in'); });
    });
  }

  function _decodeUtfChar(str) {
    return str.replace(/\\u([0-9a-fA-F]{4})/g, function (_, hex) {
      return String.fromCharCode(parseInt(hex, 16));
    });
  }

  // ─── Data fetching ──────────────────────────────────────────────

  function _fetchBaseConfig() {
    var handle = window.RSProduct.config.productHandle;
    var url = '/products/' + handle + '?view=' + LIQUID_VIEW;
    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) { return data; });
  }

  function _fetchLetters() {
    if (!window.RSGraphQL || !window.RSGraphQL.fetchCustomizerLetters) {
      return Promise.resolve({});
    }
    var filterBy = _baseConfig ? _baseConfig.product_title : null;
    return window.RSGraphQL.fetchCustomizerLetters(METAOBJECT_TYPE, { filterBy: filterBy })
      .then(function (rawLetters) {
        if (!Array.isArray(rawLetters)) return {};
        var letters = {};
        rawLetters.forEach(function (letter) {
          if (!letter || !letter.key || !letter.image) return;
          letters[letter.key] = {
            url: letter.image.url,
            width: letter.image.width || 150,
            height: letter.image.height || 150,
            name: letter.name || '',
          };
        });
        return letters;
      });
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundUrl() {
    if (!_backgroundsData) return null;
    if (_backgroundsData['default']) return _backgroundsData['default'];
    var keys = Object.keys(_backgroundsData).filter(function (k) { return _backgroundsData[k]; });
    return keys.length > 0 ? _backgroundsData[keys[0]] : null;
  }

  // ─── Letter data lookup ─────────────────────────────────────────

  function _getLetterData(char) {
    if (!_letters) return null;

    if (char === '_') {
      var spacerSettings = _baseConfig && _baseConfig.settings && _baseConfig.settings.spacer;
      if (spacerSettings && spacerSettings.image) {
        return { char: '_', url: spacerSettings.image, type: 'spacer' };
      }
      return null;
    }

    if (_iconMapping[char]) {
      var mapped = _iconMapping[char];
      if (_letters[mapped]) return _letters[mapped];
    }

    var upper = char.toUpperCase();
    if (_letters[upper]) return _letters[upper];

    var found = Object.keys(_letters).find(function (k) { return k.toUpperCase() === upper; });
    return found ? _letters[found] : null;
  }

  function _shouldExtendOrnament(elements, totalWidth, zoneWidth) {
    var letterCount = elements.filter(function (el) { return el.type !== 'spacer'; }).length;
    return totalWidth > zoneWidth || letterCount >= LETTER_COUNT_THRESHOLD;
  }

  // ─── DOM creation helpers ─────────────────────────────────────

  function _getCustomizerView() {
    if (!_injectedSlideId) return null;
    var slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    return slide.querySelector('.customizer-view');
  }

  function _createElement(type, data, styles) {
    var img = document.createElement('img');
    var isLetter = type !== 'spacer';

    img.src = _optimizeImageUrl(data.url || data.image, { height: ELEMENT_IMAGE_HEIGHT });
    img.alt = isLetter ? (data.char || '') : 'spacer';
    img.className = isLetter ? 'rs-mint__letter' : 'rs-mint__spacer';

    var heightValue = (100 * (isLetter ? FONT_ICON_SCALE_FACTOR : BEAD_SCALE_FACTOR)) + '%';
    var defaultStyles = {
      height: heightValue,
      width: 'auto',
      'object-fit': 'contain',
      'flex-shrink': '0',
      position: 'relative',
      display: 'block',
    };

    var combined = Object.assign({}, defaultStyles, styles || {});
    img.style.cssText = Object.entries(combined)
      .map(function (pair) { return pair[0] + ': ' + pair[1]; })
      .join('; ');

    return img;
  }

  function _createInsertionZone(dynamicWidth) {
    var insertionZoneData = _baseConfig && _baseConfig.settings && _baseConfig.settings.insertion_zone;
    if (!insertionZoneData) return document.createElement('div');

    var zone = document.createElement('div');
    zone.className = 'rs-mint__insertion-zone';

    var zoneWidth = dynamicWidth || insertionZoneData.width;
    zone.style.cssText = [
      'position: absolute',
      'left: ' + insertionZoneData.x_position + '%',
      'top: ' + insertionZoneData.y_position + '%',
      'width: ' + zoneWidth + '%',
      'height: ' + insertionZoneData.height + '%',
      'display: flex',
      'flex-direction: row',
      'align-items: center',
      'justify-content: center',
      'flex-wrap: nowrap',
      'z-index: 2',
      'box-sizing: border-box',
      'overflow: visible',
    ].join('; ');

    return zone;
  }

  // ─── Three-part ornament structure ─────────────────────────────

  function _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneWidthPercent) {
    var baseLayer = canvas.querySelector('.rs-mint__base-layer');
    if (!baseLayer) return;

    var scaleContainer = baseLayer.querySelector('.rs-mint__scale-container');
    if (!scaleContainer) return;

    var insertionZoneStartX = insertionZoneData.x_position;
    var insertionZoneEndX = insertionZoneStartX + insertionZoneWidthPercent;

    var extensionWidth = insertionZoneWidthPercent;
    var tailPosition = insertionZoneEndX;

    var isExtended = insertionZoneWidthPercent > insertionZoneData.width;
    var scaleValue = isExtended ? EXTENDED_SCALE_FACTOR : CONTRACTED_SCALE_FACTOR;

    scaleContainer.style.transform = 'scale(' + scaleValue + ')';

    var extensionElement = scaleContainer.querySelector('.rs-mint__extension');
    var tailElement = scaleContainer.querySelector('.rs-mint__tail');

    if (extensionElement) {
      extensionElement.style.width = extensionWidth + '%';
      extensionElement.style.left = insertionZoneStartX + '%';
    }

    if (tailElement) {
      tailElement.style.left = tailPosition + '%';
    }
  }

  function _setupThreePartStructure(scaleContainer, baseSettings) {
    var partHeight = '100%';
    var measurements = {};
    var loadPromises = [];

    var baseLayerHeight = scaleContainer.offsetHeight;

    if (baseSettings.head) {
      var tempHead = document.createElement('img');
      tempHead.src = _optimizeImageUrl(baseSettings.head, { width: BASE_IMAGE_WIDTH });
      tempHead.style.cssText = 'position:absolute;visibility:hidden;height:' + baseLayerHeight + 'px;width:auto;';
      scaleContainer.appendChild(tempHead);
      loadPromises.push(new Promise(function (resolve) {
        tempHead.onload = function () {
          measurements.headWidth = (tempHead.offsetWidth / scaleContainer.offsetWidth) * 100;
          tempHead.remove();
          resolve();
        };
        tempHead.onerror = function () {
          measurements.headWidth = 0;
          tempHead.remove();
          resolve();
        };
      }));
    } else {
      measurements.headWidth = 0;
    }

    if (baseSettings.tail) {
      var tempTail = document.createElement('img');
      tempTail.src = _optimizeImageUrl(baseSettings.tail, { width: BASE_IMAGE_WIDTH });
      tempTail.style.cssText = 'position:absolute;visibility:hidden;height:' + baseLayerHeight + 'px;width:auto;';
      scaleContainer.appendChild(tempTail);
      loadPromises.push(new Promise(function (resolve) {
        tempTail.onload = function () {
          measurements.tailWidth = (tempTail.offsetWidth / scaleContainer.offsetWidth) * 100;
          tempTail.remove();
          resolve();
        };
        tempTail.onerror = function () {
          measurements.tailWidth = 0;
          tempTail.remove();
          resolve();
        };
      }));
    } else {
      measurements.tailWidth = 0;
    }

    Promise.all(loadPromises).then(function () {
      _createThreePartElements(scaleContainer, baseSettings, measurements, partHeight);
    }).catch(function () {
      measurements.headWidth = measurements.headWidth || 0;
      measurements.tailWidth = measurements.tailWidth || 0;
      _createThreePartElements(scaleContainer, baseSettings, measurements, partHeight);
    });
  }

  function _createThreePartElements(scaleContainer, baseSettings, measurements, partHeight) {
    var insertionZoneData = _baseConfig && _baseConfig.settings && _baseConfig.settings.insertion_zone;
    var insertionZoneX = insertionZoneData ? insertionZoneData.x_position : DEFAULT_INSERTION_ZONE_X;
    var tailStartPosition = 100 - measurements.tailWidth;
    var extensionStartPosition = insertionZoneX;
    var extensionWidth = tailStartPosition - extensionStartPosition;

    if (baseSettings.head) {
      var headImg = document.createElement('img');
      headImg.className = 'rs-mint__head';
      headImg.src = _optimizeImageUrl(baseSettings.head, { width: BASE_IMAGE_WIDTH });
      headImg.alt = 'Mint Ornament Head';
      headImg.style.cssText = [
        'position: absolute',
        'top: 0',
        'left: 0',
        'height: ' + partHeight,
        'width: auto',
        'object-fit: contain',
        'z-index: 1',
      ].join('; ');
      scaleContainer.appendChild(headImg);
    }

    if (baseSettings.extension && extensionWidth > 0) {
      var extensionImg = document.createElement('img');
      extensionImg.className = 'rs-mint__extension';
      extensionImg.src = _optimizeImageUrl(baseSettings.extension, { width: BASE_IMAGE_WIDTH });
      extensionImg.alt = 'Mint Ornament Extension';
      extensionImg.style.cssText = [
        'position: absolute',
        'top: 0',
        'left: ' + extensionStartPosition + '%',
        'height: ' + partHeight,
        'width: ' + extensionWidth + '%',
        'object-fit: fill',
        'z-index: 0',
      ].join('; ');
      scaleContainer.appendChild(extensionImg);
    }

    if (baseSettings.tail) {
      var tailImg = document.createElement('img');
      tailImg.className = 'rs-mint__tail';
      tailImg.src = _optimizeImageUrl(baseSettings.tail, { width: BASE_IMAGE_WIDTH });
      tailImg.alt = 'Mint Ornament Tail';
      tailImg.style.cssText = [
        'position: absolute',
        'top: 0',
        'left: ' + tailStartPosition + '%',
        'height: ' + partHeight,
        'width: auto',
        'object-fit: contain',
        'z-index: 1',
      ].join('; ');
      scaleContainer.appendChild(tailImg);
    }

    if (insertionZoneData) {
      var canvas = scaleContainer.closest('.rs-mint__canvas');
      if (canvas) _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.width);
    }
  }

  // ─── Swing & sparkle ──────────────────────────────────────────

  function _applySwingMotion(baseLayer) {
    if (!_baseConfig || !_baseConfig.settings || !_baseConfig.settings.swing_motion) return;

    baseLayer.classList.add('rs-mint__swing');
    baseLayer.style.setProperty('--swing-degrees', SWING_MOTION_DEGREES + 'deg');
    baseLayer.style.setProperty('--swing-duration', SWING_ANIMATION_DURATION + 'ms');

    var baseSettings = _baseConfig.settings.base;
    if (baseSettings) {
      baseLayer.style.setProperty('--swing-origin-x', (baseSettings.x_anchor || 50) + '%');
      baseLayer.style.setProperty('--swing-origin-y', (baseSettings.y_anchor || 50) + '%');
      baseLayer.style.setProperty('--base-rotation', (baseSettings.rotation || 0) + 'deg');
    }
  }

  function _createSparkles(scaleContainer) {
    if (!_baseConfig || !_baseConfig.settings || !_baseConfig.settings.sparkle) return;

    var sparkleSettings = _baseConfig.settings.sparkle;
    var sparkleImage = sparkleSettings.image;
    var sparkleDimensions = sparkleSettings.dimensions;
    var baseSettings = _baseConfig.settings.base;

    if (!sparkleImage || !sparkleDimensions || !Array.isArray(sparkleDimensions) || !baseSettings) return;

    var sparkleContainer = document.createElement('div');
    sparkleContainer.className = 'rs-mint__sparkle-container';

    sparkleDimensions.forEach(function (dimension) {
      if (dimension.x_position == null || dimension.y_position == null) return;

      var sparkle = document.createElement('img');
      sparkle.className = 'rs-mint__sparkle';
      sparkle.src = _optimizeImageUrl(sparkleImage, { height: ELEMENT_IMAGE_HEIGHT });
      sparkle.alt = 'sparkle';

      var sparkleWidth = dimension.width || (SPARKLE_BASE_WIDTH + (Math.random() * SPARKLE_DELTA_WIDTH));
      var sparkleHeight = dimension.height || sparkleWidth;

      var configCrosshairOffset = 10;
      var centerOffsetX = (sparkleWidth / 2) - configCrosshairOffset;
      var centerOffsetY = (sparkleHeight / 2) - configCrosshairOffset;

      sparkle.style.left = 'calc(' + dimension.x_position + '% - ' + centerOffsetX + 'px)';
      sparkle.style.top = 'calc(' + dimension.y_position + '% - ' + centerOffsetY + 'px)';
      sparkle.style.width = sparkleWidth + 'px';
      sparkle.style.height = sparkleHeight + 'px';

      var rotation = dimension.rotation || 0;
      if (rotation !== 0) sparkle.style.transform = 'rotate(' + rotation + 'deg)';

      sparkleContainer.appendChild(sparkle);
    });

    scaleContainer.appendChild(sparkleContainer);
  }

  // ─── Build ornament DOM ───────────────────────────────────────

  function _buildOrnament(customizerView) {
    customizerView.innerHTML = '';

    var canvas = document.createElement('div');
    canvas.className = 'rs-mint__canvas';

    var baseLayer = document.createElement('div');
    baseLayer.className = 'rs-mint__base-layer';

    var baseSettings = _baseConfig && _baseConfig.settings && _baseConfig.settings.base;

    if (baseSettings) {
      var heightValue = baseSettings.height === 'auto' ? 'auto' : baseSettings.height + '%';
      var hasSwing = _baseConfig.settings.swing_motion;
      var rotationStyle = hasSwing ? '' : 'transform: rotate(' + (baseSettings.rotation || 0) + 'deg);';

      baseLayer.style.cssText = [
        'position: absolute',
        'left: ' + baseSettings.x_position + '%',
        'top: ' + baseSettings.y_position + '%',
        'width: ' + baseSettings.width + '%',
        'height: ' + heightValue,
        rotationStyle,
        'transform-origin: ' + (baseSettings.x_anchor || 0) + '% ' + (baseSettings.y_anchor || 0) + '%',
        'z-index: 1',
      ].filter(Boolean).join('; ');
    }

    canvas.appendChild(baseLayer);
    customizerView.appendChild(canvas);

    var scaleContainer = document.createElement('div');
    scaleContainer.className = 'rs-mint__scale-container';
    scaleContainer.style.cssText = [
      'position: absolute',
      'top: 0',
      'left: 0',
      'width: 100%',
      'height: 100%',
      'transform-origin: ' + ((baseSettings && baseSettings.x_anchor) || 50) + '% ' + ((baseSettings && baseSettings.y_anchor) || 50) + '%',
    ].join('; ');
    baseLayer.appendChild(scaleContainer);

    if (baseSettings) {
      _setupThreePartStructure(scaleContainer, baseSettings);
    }

    var contentLayer = document.createElement('div');
    contentLayer.className = 'rs-mint__content-layer';
    contentLayer.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:2;';
    scaleContainer.appendChild(contentLayer);

    _applySwingMotion(baseLayer);

    if (_baseConfig.settings && _baseConfig.settings.swing_motion) {
      _createSparkles(scaleContainer);
    }

    return canvas;
  }

  // ─── Content rendering ──────────────────────────────────────────

  function _renderContent(text) {
    var customizerView = _getCustomizerView();
    if (!customizerView) return;

    var canvas = customizerView.querySelector('.rs-mint__canvas');
    if (!canvas) canvas = _buildOrnament(customizerView);

    var contentLayer = canvas.querySelector('.rs-mint__content-layer');
    if (!contentLayer) return;

    contentLayer.innerHTML = '';

    if (text && text.trim()) {
      _renderLettersWithText(canvas, contentLayer, text.trim());
    } else {
      _renderSpacerContent(canvas, contentLayer);
    }
  }

  function _renderLettersWithText(canvas, container, text) {
    if (_isRendering) return;
    _isRendering = true;

    if (!_baseConfig || !_baseConfig.settings) { _isRendering = false; return; }
    if (!_letters || Object.keys(_letters).length === 0) { _isRendering = false; return; }

    var elements = text.split('').map(function (char) {
      var ld = _getLetterData(char);
      if (!ld) return null;
      return Object.assign({ char: char, type: ld.type || 'letter' }, ld);
    }).filter(Boolean);

    if (elements.length === 0) {
      _renderSpacerContent(canvas, container);
      _isRendering = false;
      return;
    }

    var spacerSettings = _baseConfig.settings.spacer;
    var insertionZoneData = _baseConfig.settings.insertion_zone;

    container.innerHTML = '';

    var insertionZoneEl = _createInsertionZone();
    container.appendChild(insertionZoneEl);

    if (insertionZoneData) {
      _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.width);
    }

    if (!spacerSettings || !spacerSettings.image) {
      // No spacer — measure letters, determine mode, render letters only
      var testLetters = [];
      elements.forEach(function (element) {
        var testImg = document.createElement('img');
        testImg.src = _optimizeImageUrl(element.url, { height: ELEMENT_IMAGE_HEIGHT });
        testImg.style.cssText = 'position:absolute;left:0;top:0;height:' + (100 * FONT_ICON_SCALE_FACTOR) + '%;width:auto;object-fit:contain;visibility:hidden;';
        insertionZoneEl.appendChild(testImg);
        testLetters.push({ element: element, testImg: testImg });
      });

      var letterPromises = testLetters.map(function (item) {
        return new Promise(function (resolve) {
          if (item.testImg.complete) { resolve(); return; }
          item.testImg.onload = resolve;
          item.testImg.onerror = resolve;
        });
      });

      Promise.all(letterPromises).then(function () {
        var origWidth = insertionZoneEl.offsetWidth;
        var widths = testLetters.map(function (item) { return item.testImg.offsetWidth; });
        var totalWidth = widths.reduce(function (s, w) { return s + w; }, 0);

        testLetters.forEach(function (item) { item.testImg.remove(); });
        container.innerHTML = '';

        var activeZone;
        if (_shouldExtendOrnament(elements, totalWidth, origWidth)) {
          if (insertionZoneData && insertionZoneData.expanded_width) {
            activeZone = _createInsertionZone(insertionZoneData.expanded_width);
            container.appendChild(activeZone);
            _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.expanded_width);
          }
        } else if (insertionZoneData) {
          activeZone = _createInsertionZone(insertionZoneData.width);
          container.appendChild(activeZone);
          _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.width);
        }

        if (activeZone) {
          elements.forEach(function (element, idx) {
            var img = _createElement(element.type, element, { width: widths[idx] + 'px', order: idx });
            activeZone.appendChild(img);
            _triggerFadeIn(img);
          });
        }
        _isRendering = false;
      }).catch(function () { _isRendering = false; });
      return;
    }

    // Spacer available — measure letters AND spacer, then render with spacers
    var testElements = [];
    elements.forEach(function (element) {
      var testImg = document.createElement('img');
      testImg.src = _optimizeImageUrl(element.url, { height: ELEMENT_IMAGE_HEIGHT });
      var scaleFactor = element.type === 'spacer' ? BEAD_SCALE_FACTOR : FONT_ICON_SCALE_FACTOR;
      testImg.style.cssText = 'position:absolute;left:0;top:0;height:' + (100 * scaleFactor) + '%;width:auto;object-fit:contain;visibility:hidden;';
      insertionZoneEl.appendChild(testImg);
      testElements.push({ element: element, testImg: testImg });
    });

    var testSpacer = document.createElement('img');
    testSpacer.src = _optimizeImageUrl(spacerSettings.image, { height: ELEMENT_IMAGE_HEIGHT });
    testSpacer.style.cssText = 'position:absolute;left:0;top:0;height:' + (100 * BEAD_SCALE_FACTOR) + '%;width:auto;object-fit:contain;visibility:hidden;';
    insertionZoneEl.appendChild(testSpacer);

    var loadPromises = testElements.map(function (item) {
      return new Promise(function (resolve) {
        if (item.testImg.complete) { resolve(); return; }
        item.testImg.onload = resolve;
        item.testImg.onerror = resolve;
      });
    });

    loadPromises.push(new Promise(function (resolve) {
      if (testSpacer.complete) { resolve(); return; }
      testSpacer.onload = resolve;
      testSpacer.onerror = resolve;
    }));

    Promise.all(loadPromises).then(function () {
      var originalZoneWidth = insertionZoneEl.offsetWidth;
      var actualSpacerWidth = testSpacer.offsetWidth;
      var elementWidths = testElements.map(function (item) { return item.testImg.offsetWidth; });
      var totalWidth = elementWidths.reduce(function (sum, w) { return sum + w; }, 0);

      testElements.forEach(function (item) { item.testImg.remove(); });
      testSpacer.remove();
      container.innerHTML = '';

      var needsExtend = _shouldExtendOrnament(elements, totalWidth, originalZoneWidth);
      var finalZoneWidth = originalZoneWidth;
      var activeZone;

      if (needsExtend) {
        if (insertionZoneData && insertionZoneData.expanded_width) {
          activeZone = _createInsertionZone(insertionZoneData.expanded_width);
          container.appendChild(activeZone);
          finalZoneWidth = activeZone.offsetWidth;
          _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.expanded_width);
        }
      } else if (insertionZoneData) {
        activeZone = _createInsertionZone(insertionZoneData.width);
        container.appendChild(activeZone);
        finalZoneWidth = activeZone.offsetWidth;
        _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.width);
      }

      if (!activeZone) { _isRendering = false; return; }

      var spacerCount = 0;
      var adjustedSpacerWidth = actualSpacerWidth;

      if (totalWidth < finalZoneWidth && actualSpacerWidth > 0) {
        var remainingWidth = finalZoneWidth - totalWidth;
        if (elements.length === 10) {
          remainingWidth += elements.length;
        }
        spacerCount = Math.round(remainingWidth / actualSpacerWidth);
        if (spacerCount > 0) {
          adjustedSpacerWidth = remainingWidth / spacerCount;
        }
      }

      var spacersBeforeCount = Math.floor(spacerCount / 2);
      var spacersAfterCount = spacerCount - spacersBeforeCount;
      var elementIndex = 0;

      for (var i = 0; i < spacersBeforeCount; i++) {
        var sp = _createElement('spacer', { url: spacerSettings.image }, { width: adjustedSpacerWidth + 'px', order: elementIndex });
        activeZone.appendChild(sp);
        _triggerFadeIn(sp);
        elementIndex++;
      }

      elements.forEach(function (element, idx) {
        var img = _createElement(element.type, element, { width: elementWidths[idx] + 'px', order: elementIndex });
        activeZone.appendChild(img);
        _triggerFadeIn(img);
        elementIndex++;
      });

      for (var j = 0; j < spacersAfterCount; j++) {
        var sp2 = _createElement('spacer', { url: spacerSettings.image }, { width: adjustedSpacerWidth + 'px', order: elementIndex });
        activeZone.appendChild(sp2);
        _triggerFadeIn(sp2);
        elementIndex++;
      }

      _isRendering = false;
    }).catch(function () { _isRendering = false; });
  }

  function _renderSpacerContent(canvas, container) {
    var spacerSettings = _baseConfig && _baseConfig.settings && _baseConfig.settings.spacer;
    if (!spacerSettings || !spacerSettings.image) return;

    var insertionZoneData = _baseConfig.settings.insertion_zone;
    container.innerHTML = '';

    var insertionZoneEl = _createInsertionZone();
    container.appendChild(insertionZoneEl);

    if (insertionZoneData) {
      _scaleExtensionAndPositionTail(canvas, insertionZoneData, insertionZoneData.width);
    }

    var testSpacer = document.createElement('img');
    testSpacer.src = _optimizeImageUrl(spacerSettings.image, { height: ELEMENT_IMAGE_HEIGHT });
    testSpacer.style.cssText = 'position:absolute;left:0;top:0;height:' + (100 * BEAD_SCALE_FACTOR) + '%;width:auto;object-fit:contain;visibility:hidden;';
    insertionZoneEl.appendChild(testSpacer);

    new Promise(function (resolve) {
      if (testSpacer.complete) { resolve(); return; }
      testSpacer.onload = resolve;
      testSpacer.onerror = resolve;
    }).then(function () {
      var zoneWidth = insertionZoneEl.offsetWidth;
      var spacerWidth = testSpacer.offsetWidth;
      testSpacer.remove();

      if (spacerWidth <= 0 || zoneWidth <= 0) return;

      var spacerCount = Math.floor(zoneWidth / spacerWidth);

      if (spacerCount < MIN_SPACER_COUNT) {
        var densityAdjustedWidth = spacerWidth * SPACER_DENSITY_FACTOR;
        var calculatedCount = Math.ceil(zoneWidth / densityAdjustedWidth);
        spacerCount = Math.max(MIN_SPACER_COUNT, calculatedCount);
      }

      var minSpacerWidth = spacerWidth * MIN_SPACER_WIDTH_RATIO;
      var maxPossibleCount = Math.floor(zoneWidth / minSpacerWidth);
      spacerCount = Math.min(spacerCount, maxPossibleCount);

      if (spacerCount <= 0 || !Number.isFinite(spacerCount)) return;

      var adjustedSpacerWidth = zoneWidth / spacerCount;

      for (var i = 0; i < spacerCount; i++) {
        var spacerElement = _createElement('spacer', { url: spacerSettings.image }, {
          width: adjustedSpacerWidth + 'px',
          order: i,
        });
        insertionZoneEl.appendChild(spacerElement);
        _triggerFadeIn(spacerElement);
      }
    });
  }

  // ─── Icon button handling ─────────────────────────────────────

  function _handleIconButtonClick(event) {
    var btn = event.target.closest('[data-utf-char]');
    if (!btn || !_letters) return;

    var sectionEl = document.getElementById('MainProduct--' + window.RSProduct.sectionId);
    if (!sectionEl || !sectionEl.contains(btn)) return;

    var title = (btn.dataset.title || '').trim();
    var rawUtf = btn.dataset.utfChar || '';
    if (!title || !rawUtf) return;

    var decoded = _decodeUtfChar(rawUtf);
    if (!decoded) return;

    var keys = Object.keys(_letters);
    var match = keys.find(function (k) {
      return (
        k.toLowerCase() === title.toLowerCase() ||
        ((_letters[k].name || '').toLowerCase() === title.toLowerCase())
      );
    });

    if (match) {
      _iconMapping[decoded] = match;
    }

    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderContent(_getMtoText());
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    var variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    var backgroundUrl = _getBackgroundUrl();
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    var customizerView = _getCustomizerView();
    if (customizerView) {
      _buildOrnament(customizerView);
      _renderContent(_getMtoText());
    }

    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      _renderContent(_getMtoText());
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    var baseConfig;
    try {
      baseConfig = await _fetchBaseConfig();
    } catch (err) {
      console.error('[MintOrnament] Base config fetch failed:', err);
      return;
    }

    _baseConfig = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};

    var initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    var variantNames = baseConfig.variant_names || {};
    var fullName = variantNames[initialVariantId] || '';
    var parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    var backgroundUrl = _getBackgroundUrl();
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    var letters = {};
    try {
      letters = await _fetchLetters();
    } catch (err) {
      console.error('[MintOrnament] Letter fetch failed:', err);
    }
    _letters = letters || {};

    if (_injectedSlideId) {
      var customizerView = _getCustomizerView();
      if (customizerView) {
        _buildOrnament(customizerView);
        _renderContent(_getMtoText());
      }
    }

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    document.addEventListener('click', _handleIconButtonClick, true);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
